import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import classNames from 'classnames'
import * as React from 'react'

import {useHeightResizeObserver, useWidthResizeObserver} from '../../../../hooks/useResizeObserver'
import type {ChangeLogRow} from '../../../../services/rest'
import {restApi} from '../../../../services/rest'
import {stringifyId} from '../../../../types'
import type {ChangeId, BuildTypeId} from '../../../../types'
import {emptyArray} from '../../../../utils/empty'
import Grid from '../../../common/Grid/Grid'
import RowGroup from '../../../common/Grid/RowGroup'
import Pager from '../../../common/Pager/Pager.container'
import {PagerGroup} from '../../../common/Pager/Pager.types'
import {useSequenceLoaderReadyHandler} from '../../../common/SequenceLoader/SequenceLoader.hooks'
import type {SeqProps} from '../../../common/SequenceLoader/SequenceLoader.types'
import Change from '../Change/Change'
import {ChangeContext, ChangesGraphIndentContext, ChangeView} from '../Change/Change.context'
import {useChangesFilter} from '../Changes.hooks'

import Build from './Build/Build'
import ChangesGraph from './ChangesGraph/ChangesGraph'
import {getChangeLogArg} from './ChangesLog.utils'

import styles from './ChangesLog.css'

type Props = SeqProps & {
  className?: string
  locator: string | null
  buildTypeId?: BuildTypeId | null | undefined
}

function ChangesLog({className, locator, buildTypeId}: Props) {
  const {filterOptions} = useChangesFilter(buildTypeId)
  const {showFiles, withExpandableLines, withGraph, withRevision, withChangesActions, changeView} =
    React.useContext(ChangeContext)

  const changeLogArg = React.useMemo(
    () =>
      locator != null
        ? getChangeLogArg({
            locator,
            changesOptions: {withFiles: showFiles, withUpdatePager: true},
            withGraph,
          })
        : null,
    [locator, withGraph, showFiles],
  )

  const [fetchChangeLog, {rows, isChangeLogLoading, isChangeLogReady}] =
    restApi.endpoints.getChangeLog.useLazyQuery({
      selectFromResult: ({data, currentData}) => ({
        rows: data?.rows ?? emptyArray,
        isChangeLogLoading: data !== currentData,
        isChangeLogReady: data != null,
      }),
    })

  React.useEffect(() => {
    if (changeLogArg != null) {
      fetchChangeLog(changeLogArg, true)
    }
  }, [changeLogArg, fetchChangeLog])

  const [expandedChange, setExpandedChange] = React.useState<ChangeId | null>(null)
  const toggleExpandedChange = React.useCallback(
    (id: ChangeId) => {
      setExpandedChange(expandedChange !== id ? id : null)
    },
    [expandedChange, setExpandedChange],
  )

  const [width, graphRef] = useWidthResizeObserver()
  const [height, changesRef] = useHeightResizeObserver()

  useSequenceLoaderReadyHandler(isChangeLogReady)

  const classes = classNames(className, {
    [styles.overlay]: isChangeLogLoading,
    [styles.preview]: changeView === ChangeView.PREVIEW,
    [styles.overall]: changeView === ChangeView.OVERALL,
  })

  const renderRow = (row: ChangeLogRow) => {
    const buildId = row.build?.id
    const changeId = row.change?.id
    if (buildId == null && changeId == null) {
      return null
    }

    return (
      <RowGroup key={stringifyId(buildId ?? changeId)} span={2} gridId="changeLog">
        {buildId != null ? <Build buildId={buildId} /> : null}
        {changeId != null ? (
          <Change
            expanded={showFiles || (withExpandableLines && expandedChange === changeId)}
            changeId={changeId}
            buildTypeId={buildTypeId}
            toggleExpandedChange={toggleExpandedChange}
            withAuthor={
              (filterOptions.username == null || filterOptions.username === '') &&
              (filterOptions.userId == null || filterOptions.userId === '')
            }
          />
        ) : null}
      </RowGroup>
    )
  }

  const renderList = () => (
    <div style={width !== 0 ? {marginLeft: `-${width}px`} : {}}>
      <Grid
        className={classNames(styles.grid, {
          [styles.showRevision]: withRevision,
          [styles.showActions]: withChangesActions,
        })}
        gridId="changeLog"
      >
        {rows.map(renderRow)}
      </Grid>
    </div>
  )

  const renderLoader = () => (
    <div className={styles.loading}>
      <LoaderInline>{'Loading...'}</LoaderInline>
    </div>
  )

  const renderNoChanges = () => <div className={styles.empty}>{'There are no changes found'}</div>

  if (!isChangeLogReady) {
    return null
  }

  return (
    <div className={classes}>
      <div className={styles.changesWrapper}>
        <div ref={graphRef}>
          <ChangesGraph
            className={styles.graph}
            changesClassName={styles.changes}
            height={height}
            changeLogArg={changeLogArg}
          />
        </div>
        <ChangesGraphIndentContext.Provider value={width ?? 0}>
          <div className={styles.changes} ref={changesRef}>
            {rows.length > 0 ? renderList() : null}
            {rows.length === 0 && isChangeLogLoading ? renderLoader() : null}
            {rows.length === 0 && !isChangeLogLoading ? renderNoChanges() : null}
          </div>
        </ChangesGraphIndentContext.Provider>
      </div>
      <Pager isLoading={isChangeLogLoading} group={PagerGroup.CHANGE_LOG} />
    </div>
  )
}

export default React.memo(ChangesLog)
